import React, { useState } from "react";
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import { ProcessedFileDialog } from "./ProcessedFileDialog";
import { Switch } from "./switch"; 
import { Text } from "./text";
import { Button } from "./button";

const REQUIRED_FILES = {
  actaConstitutiva: "Acta Constitutiva",
  constanciaSituacionFiscal: "Constancia de Situación Fiscal",
  actasAsambleas: "Actas de Asambleas",
  poderes: "Poderes",
  comprobanteDomicilio: "Comprobante de Domicilio",
  identificacionOficial: "Identificación oficial del apoderado",
  acuseReciboDeclaracion: "Acuse de Recibo de Declaración Anual",
  declaracionEjercicio: "Declaración del Ejercicio",
  estadosFinancierosExcel: "Estados Financieros Declaración Anual (Excel)",
  estadosFinancieros: "Estados Financieros (Contador)",
  estadosFinancierosParciales: "Estados Financieros Parciales (Contador)",
  cedulaContador: "Cédula Profesional del Contador",
  curriculumEmpresarial: "Currículum Empresarial",
  contrato: "Contrato",
};

const LinkFiles = ({ linkData, onToggleRequired }) => {
  const [isProcessedDialogOpen, setIsProcessedDialogOpen] = useState(false);
  const [processedDialogData, setProcessedDialogData] = useState({});
  const downloadUrlsRef = React.useRef({});

  const handleOpenFile = async (filePath) => {
    try {
      let url = downloadUrlsRef.current[filePath];
      if (!url) {
        const storageRef = ref(storage, filePath);
        url = await getDownloadURL(storageRef);
        downloadUrlsRef.current[filePath] = url;
      }
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error opening file:", error);
      alert("No se pudo abrir el archivo.");
    }
  };
  
  const handleViewResults = async (fileInfo, fileType) => {
    const fileId = `${fileType}_${fileInfo.uploadedAt}`;
    const fullFileData = linkData.allFiles.find(f => f.id === fileId);
    
    if (!fullFileData || !fullFileData.results) {
      alert("Los datos procesados para este archivo no están disponibles.");
      return;
    }
    
    try {
      let url = downloadUrlsRef.current[fullFileData.filePath];
      if (!url) {
        const fileRef = ref(storage, fullFileData.filePath);
        url = await getDownloadURL(fileRef);
        downloadUrlsRef.current[fullFileData.filePath] = url;
      }

      setProcessedDialogData({
        processedFileData: fullFileData,
        fileType: fileType,
        fileName: fileInfo.name,
        fileLabel: REQUIRED_FILES[fileType],
        pdfUrl: url,
      });
      setIsProcessedDialogOpen(true);

    } catch (error) {
       alert("No se pudo obtener la URL del archivo para la vista previa.");
    }
  };

  const renderStatusIcon = (status) => {
    switch (status) {
      case "procesado":
        return <CheckCircleIcon className="w-5 h-5 text-green-500" title="Procesado" />;
      case "error":
        return <ExclamationCircleIcon className="w-5 h-5 text-red-500" title="Error" />;
      default:
        return <ClockIcon className="w-5 h-5 text-yellow-500" title="En Proceso" />;
    }
  };

  return (
    <>
      <div className="space-y-6">
        {Object.entries(REQUIRED_FILES).map(([fileId, label]) => {
          const fileConfig = linkData.files?.[fileId] || {};
          const isRequired = fileConfig.required || false;
          const files = fileConfig.fileNames || [];
          const isContract = fileId === 'contrato';
          const isUSDocument = ['actaConstitutiva', 'constanciaSituacionFiscal', 'actasAsambleas', 'poderes', 'comprobanteDomicilio', 'identificacionOficial', 'acuseReciboDeclaracion', 'declaracionEjercicio', 'estadosFinancierosExcel', 'estadosFinancieros', 'estadosFinancierosParciales', 'cedulaContador', 'curriculumEmpresarial'].includes(fileId);

          return (
            <div key={fileId} className={`border rounded-lg overflow-hidden ${isContract ? 'ring-2 ring-blue-200 bg-blue-50/30' : isUSDocument ? 'ring-1 ring-gray-200 bg-gray-50/20' : ''}`}>
              <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between gap-4">
                <h3 className="font-medium text-sm text-gray-800">{label}</h3>
                <div className="flex items-center gap-2">
                  <Text size="sm" color={isRequired ? "default" : "gray"}>
                    {isRequired ? "Solicitado" : "No solicitado"}
                  </Text>
                  <Switch
                    checked={isRequired}
                    onChange={() => onToggleRequired(fileId, !isRequired)}
                    name={`required-${fileId}`}
                  />
                </div>
              </div>

              <div className="divide-y divide-gray-100">
                {files.length > 0 ? (
                  files.map((file, index) => (
                    <div
                      key={`${fileId}-${index}`}
                      className="p-4 flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {renderStatusIcon(file.status)}
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-gray-700 truncate" title={file.name}>
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            Subido el {new Date(file.uploadedAt).toLocaleString("es-MX")}
                          </p>
                          {file.status === 'error' && file.error && (
                             <p className="text-xs text-red-600">Error: {file.error}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        {file.status === 'procesado' && (
                           <Button size="xs" color="white" onClick={() => handleViewResults(file, fileId)}>
                             Ver resultados
                           </Button>
                        )}
                        <Button size="xs" color="white" onClick={() => handleOpenFile(file.path)}>
                          <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                          <span>Abrir</span>
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-sm text-gray-400">
                    No hay archivos cargados para este requisito.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <ProcessedFileDialog
        open={isProcessedDialogOpen}
        onClose={() => setIsProcessedDialogOpen(false)}
        processedFileData={processedDialogData.processedFileData}
        fileType={processedDialogData.fileType}
        fileName={processedDialogData.fileName}
        fileLabel={processedDialogData.fileLabel}
        pdfUrl={processedDialogData.pdfUrl}
      />
    </>
  );
};

export default LinkFiles;
